import LegalLayout, { Section, Card, Grid, InfoBox, ContactCTA } from "@/components/chalky/LegalLayout"

interface Props {
  last_updated: string
}

export default function CookiePolicy({ last_updated }: Props) {
  return (
    <LegalLayout
      title="Politique des"
      titleHighlight="Cookies"
      subtitle="Information transparente sur l'utilisation des cookies sur le site chalky.fr"
      lastUpdated={last_updated}
    >
      <Section number="1" title="Qu'est-ce qu'un cookie ?">
        <Card>
          <p className="font-medium text-white mb-3">
            <i className="fa-solid fa-circle-info text-slate-500 mr-2" />
            Définition simple
          </p>
          <p className="text-slate-400 leading-relaxed">
            Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, smartphone, tablette) lors de la visite d'un site web. Il permet au site de se "souvenir" de certaines informations sur votre visite et vos préférences.
          </p>
        </Card>
      </Section>

      <Section number="2" title="Notre approche minimaliste">
        <p className="text-slate-400 leading-relaxed mb-6">
          Chez CHALKY, nous croyons en une approche respectueuse de vos données. C'est pourquoi nous utilisons un minimum de cookies, uniquement ceux nécessaires au bon fonctionnement du site.
        </p>
        <Grid cols={3}>
          <Card className="text-center">
            <i className="fa-solid fa-check text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Ce que nous utilisons</p>
            <p className="text-sm text-slate-400">Uniquement des cookies techniques essentiels</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-xmark text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Ce que nous n'utilisons pas</p>
            <p className="text-sm text-slate-400">Aucun cookie de tracking ou publicitaire</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-lock text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Votre confidentialité</p>
            <p className="text-sm text-slate-400">Aucune donnée partagée avec des tiers</p>
          </Card>
        </Grid>
      </Section>

      <Section number="3" title="Les cookies que nous utilisons">
        <p className="text-slate-400 mb-6">
          Ces cookies sont indispensables au fonctionnement du site. Ils ne collectent aucune information personnelle identifiable et ne peuvent pas être désactivés.
        </p>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-gear text-slate-500 mr-2" />
              Cookie de session
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Maintient votre session utilisateur</li>
              <li>Permet la navigation sécurisée</li>
              <li>Expire à la fermeture du navigateur</li>
              <li>Aucune donnée personnelle stockée</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-shield-halved text-slate-500 mr-2" />
              Protection CSRF
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Sécurise les formulaires de contact</li>
              <li>Prévient les attaques malveillantes</li>
              <li>Généré automatiquement par Rails</li>
              <li>Essentiel pour votre sécurité</li>
            </ul>
          </Card>
        </Grid>
      </Section>

      <Section number="4" title="Ce que nous ne faisons pas">
        <Grid>
          <Card>
            <p className="font-medium text-white mb-4">
              <i className="fa-solid fa-ban text-slate-500 mr-2" />
              Aucun tracking publicitaire
            </p>
            <ul className="list-disc pl-5 text-sm text-slate-400 space-y-2">
              <li>Pas de Google Analytics ou similaires</li>
              <li>Aucun pixel de tracking Facebook</li>
              <li>Pas de cookies publicitaires</li>
              <li>Aucun partage avec des régies publicitaires</li>
              <li>Pas de profilage comportemental</li>
              <li>Aucune revente de données</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-4">
              <i className="fa-solid fa-fingerprint text-slate-500 mr-2" />
              Aucune donnée personnelle
            </p>
            <ul className="list-disc pl-5 text-sm text-slate-400 space-y-2">
              <li>Pas de collecte d'informations personnelles</li>
              <li>Aucun stockage d'adresses email</li>
              <li>Pas d'historique de navigation</li>
              <li>Aucune géolocalisation</li>
              <li>Pas d'empreinte digitale (fingerprinting)</li>
              <li>Aucun cookie tiers</li>
            </ul>
          </Card>
        </Grid>
      </Section>

      <Section number="5" title="Contrôle et gestion des cookies">
        <p className="text-slate-400 mb-6">
          Bien que nous utilisions uniquement des cookies techniques essentiels, vous gardez le contrôle total sur ces paramètres.
        </p>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-sliders text-slate-500 mr-2" />
              Sur votre navigateur
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Paramètres &rarr; Confidentialité &rarr; Cookies</li>
              <li>Bloquer tous les cookies (peut limiter les fonctionnalités)</li>
              <li>Supprimer les cookies existants</li>
              <li>Mode navigation privée disponible</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">
              <i className="fa-solid fa-scale-balanced text-slate-500 mr-2" />
              Vos droits RGPD
            </p>
            <ul className="list-disc pl-4 text-sm text-slate-400 space-y-2">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement</li>
              <li>Droit à la portabilité</li>
            </ul>
          </Card>
        </Grid>
        <InfoBox icon="fa-solid fa-triangle-exclamation" title="Important à savoir">
          En désactivant tous les cookies, certaines fonctionnalités du site peuvent ne plus fonctionner correctement (formulaire de contact, navigation sécurisée). Les cookies techniques que nous utilisons sont exemptés du consentement selon la CNIL car strictement nécessaires.
        </InfoBox>
      </Section>

      <Section number="6" title="Mise à jour de cette politique">
        <p className="text-slate-400 mb-6">
          Cette politique des cookies peut être mise à jour pour refléter les évolutions de notre site ou les changements réglementaires.
        </p>
        <InfoBox icon="fa-solid fa-bell" title="Nous vous préviendrons">
          Toute modification importante sera signalée sur cette page avec la nouvelle date de mise à jour. Nous recommandons de consulter régulièrement cette politique.
        </InfoBox>
      </Section>

      <Section number="7" title="Des questions sur les cookies ?">
        <Grid cols={3}>
          <Card className="text-center">
            <i className="fa-solid fa-envelope text-slate-500 mb-3 block" />
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Email</p>
            <p className="text-sm text-slate-300">hello@chalky.fr</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-phone text-slate-500 mb-3 block" />
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Téléphone</p>
            <p className="text-sm text-slate-300">+33 6 74 23 60 80</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-location-dot text-slate-500 mb-3 block" />
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Adresse</p>
            <p className="text-sm text-slate-300">Saint-Sébastien-sur-Loire<br />Nantes, France</p>
          </Card>
        </Grid>
      </Section>

      <ContactCTA
        title="Une approche transparente des cookies"
        subtitle="Nous croyons en une utilisation responsable et minimaliste des cookies pour respecter votre vie privée."
        secondaryHref="/confidentiality"
        secondaryLabel="Politique de confidentialité"
      />
    </LegalLayout>
  )
}
